import { stringify } from 'query-string';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { stringifyOptions } from '../../../constants';
import type { CustomSummaryResponse, SummaryRequest } from '../../../summary/types.tsx';
import type { PollResponse } from '../types';

const INITIAL_POLLING_INTERVAL = 1000; // 1s
export const MAXIMUM_POLLING_INTERVAL = 5 * INITIAL_POLLING_INTERVAL; // 5s max

export const getRequestsUrl = (
	taskId: string,
	{ baseUrl, projectKey, reportId }: SummaryRequest,
) => {
	const queryParam = stringify(
		{
			jobID: taskId,
		},
		// @ts-expect-error - TS2559 - Type '{ readonly skipNull: true; readonly sort: false; }' has no properties in common with type 'StringifyOptions'.
		stringifyOptions,
	);

	return `${baseUrl}/rest/servicedesk/reports/1/reports/async/${projectKey}/${reportId}/poll?${queryParam}`;
};

const getNextInterval = (interval: number): number => {
	if (interval === 0) {
		return INITIAL_POLLING_INTERVAL;
	}
	const nextInterval = interval * 1.1;
	return Math.min(MAXIMUM_POLLING_INTERVAL, nextInterval);
};

const fetchData = (taskId: string, summaryRequest: SummaryRequest): Promise<PollResponse> => {
	const url = getRequestsUrl(taskId, summaryRequest);

	return fetchJson(url, {
		method: 'GET',
		signal: summaryRequest.signal,
	});
};

const shouldKeepPolling = (pollData: PollResponse) => {
	switch (pollData.status) {
		case 'RUNNING':
		case 'ENQUEUED':
			return true;
		default:
			return false;
	}
};

const pollWithInterval = (
	taskId: string,
	summaryRequest: SummaryRequest,
	interval: number,
): Promise<CustomSummaryResponse> =>
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	new Promise((resolve, reject: (error?: any) => void) => {
		const poll = () =>
			fetchData(taskId, summaryRequest)
				.then((pollData: PollResponse) => {
					if (shouldKeepPolling(pollData)) {
						pollWithInterval(taskId, summaryRequest, getNextInterval(interval)).then(
							resolve,
							reject,
						);
					} else if (pollData.status === 'COMPLETE' && pollData.reportDataResponse) {
						resolve(pollData.reportDataResponse);
					} else {
						reject(
							new Error(
								`Unable to fetch the report data due to unknown error. The task status was ${pollData.status}.`,
							),
						);
					}
				})
				.catch(reject);
		setTimeout(poll, interval);
	});

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default async (
	taskId: string,
	summaryRequest: SummaryRequest,
): Promise<CustomSummaryResponse> => pollWithInterval(taskId, summaryRequest, 0);
